import { useEffect, useState } from "react";
import { User, Building2, Mail, Phone, MapPin, Shield, LogOut, AtSign, Loader2, CheckCircle2, Monitor, Smartphone, ShieldCheck, X, Pencil, Plus, Trash2, Star, Truck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard } from "@/components/ui-helpers";
import { AddressFields } from "@/components/AddressFields";
import { Input } from "@/components/ui/input";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { MobileVerifyCard } from "@/pages/VerifyMobile";
import { SavedPlacesManager } from "@/components/SavedPlaces";
import { WatchedPrices } from "@/components/FeedProducts";

const ADDR_BLANK = { name: "", phone: "", line1: "", line2: "", city: "", district: "", state: "", pincode: "", country: "", gstin: "", is_default: false };
const SEC_LABEL = {
  new_signin: "New sign-in",
  password_changed: "Password changed",
  email_change_requested: "Email change requested",
  email_changed: "Email address changed",
  signed_out_all: "Signed out of all devices",
};

export default function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [newEmail, setNewEmail] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pendingEmail, setPendingEmail] = useState("");
  const [sessions, setSessions] = useState([]);
  const [secEvents, setSecEvents] = useState([]);
  const [mobileReq, setMobileReq] = useState(null);
  const loadMobileReq = () => api.get("/auth/mobile/requirement").then((r) => setMobileReq(r.data)).catch(() => {});
  useEffect(() => { loadMobileReq(); }, []);
  const onMobileVerified = async () => { await refreshUser(); loadMobileReq(); };
  const [uploading, setUploading] = useState(false);
  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 900000) { toast.error("Image too large — please use one under ~900 KB."); return; }
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", f); await api.post("/me/avatar", fd); await refreshUser(); toast.success("Picture updated"); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not upload image"); }
    finally { setUploading(false); }
  };

  const loadSessions = () => api.get("/auth/sessions").then((r) => setSessions(r.data.devices)).catch(() => {});
  useEffect(() => { loadSessions(); }, []);
  useEffect(() => { api.get("/auth/security-activity").then((r) => setSecEvents(r.data.security_changes || [])).catch(() => {}); }, []);
  const [sellerType, setSellerType] = useState("");
  useEffect(() => {
    if (user?.role === "travel_agent") api.get("/seller/application").then((r) => setSellerType(r.data?.application?.business_type || "")).catch(() => {});
  }, [user?.role]);
  const roleLabel = user?.role === "travel_agent" ? "Seller" : user?.role === "super_admin" ? "Admin" : (user?.role || "").replace(/_/g, " ");
  const forgetDevice = async (id) => {
    try { await api.delete(`/auth/sessions/${id}`); setSessions((s) => s.filter((d) => d.id !== id)); toast.success("Device removed"); }
    catch { toast.error("Could not remove device"); }
  };
  const signOutAll = async () => {
    try { await api.post("/auth/sign-out-all"); toast.success("Signed out of all devices"); await logout(); navigate("/login"); }
    catch { toast.error("Could not sign out"); }
  };
  const deviceLabel = (ua = "") => {
    const b = /Edg/.test(ua) ? "Edge" : /Chrome/.test(ua) ? "Chrome" : /Firefox/.test(ua) ? "Firefox" : /Safari/.test(ua) ? "Safari" : "Browser";
    const os = /Android/.test(ua) ? "Android" : /iPhone|iPad|iOS/.test(ua) ? "iOS" : /Windows/.test(ua) ? "Windows" : /Mac/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : "";
    return `${b}${os ? " · " + os : ""}`;
  };
  const isMobile = (ua = "") => /Mobile|Android|iPhone|iPad/.test(ua);
  const [editId, setEditId] = useState(null);
  const [editName, setEditName] = useState("");
  const renameDevice = async (id) => {
    const name = editName.trim();
    try { await api.patch(`/auth/sessions/${id}`, { name }); setSessions((s) => s.map((d) => (d.id === id ? { ...d, name } : d))); setEditId(null); toast.success("Device renamed"); }
    catch { toast.error("Could not rename device"); }
  };

  useEffect(() => { api.get("/company").then((r) => setCompany(r.data.company)).catch(() => {}); }, []);

  useEffect(() => {
    const status = searchParams.get("email");
    if (!status) return;
    if (status === "changed") toast.success("Email updated! Please log in again with your new address.", { duration: 6000 });
    else if (status === "taken") toast.error("That email is already in use by another account.");
    else if (status === "error") toast.error("The confirmation link is invalid or has expired. Please try again.");
    searchParams.delete("email");
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const submitEmailChange = async (e) => {
    e.preventDefault();
    const email = newEmail.trim().toLowerCase();
    if (!email) return;
    setSubmitting(true);
    try {
      const r = await api.post("/auth/change-email", { new_email: email, app_origin: window.location.origin });
      setPendingEmail(r.data.pending_email || email);
      setNewEmail("");
      toast.success("Confirmation email sent. Check your new inbox to finish the change.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not request an email change.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      <PageHeader title="My Profile" icon={User} />
      <SectionCard>
        <div className="flex items-center gap-4">
          <label className="group relative h-16 w-16 shrink-0 cursor-pointer" data-testid="avatar-upload-label">
            {user?.avatar
              ? <img src={user.avatar} alt="avatar" className="h-16 w-16 rounded-2xl object-cover" data-testid="avatar-img" />
              : <span className="flex h-16 w-16 items-center justify-center rounded-2xl brand-gradient-bg text-xl font-bold text-white">{initials}</span>}
            <span className="absolute inset-0 flex items-center justify-center rounded-2xl bg-black/40 text-[10px] font-semibold text-white opacity-0 transition group-hover:opacity-100">{uploading ? "…" : "Change"}</span>
            <input type="file" accept="image/*" className="hidden" onChange={uploadAvatar} data-testid="avatar-input" />
          </label>
          <div>
            <h2 className="font-brand text-xl font-bold text-slate-800">{user?.name}</h2>
            {user?.mobile_verified
              ? <span data-testid="mobile-verified-badge" className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"><ShieldCheck className="h-3 w-3" /> Mobile verified</span>
              : (mobileReq?.applies
                ? <button onClick={() => navigate("/verify-mobile")} data-testid="mobile-verify-cta" className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700 hover:bg-amber-200"><Smartphone className="h-3 w-3" /> Verify mobile</button>
                : null)}
            <p className="text-sm capitalize text-slate-500" data-testid="profile-role-label">{roleLabel}{sellerType ? ` · ${sellerType}` : ""}</p>
            <p className="mt-0.5 text-xs text-slate-400">{user?.role === "travel_agent" ? "Upload your store/brand logo" : "Upload a profile photo"} — tap the picture</p>
          </div>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          <Info icon={Mail} label="Email" value={user?.email} />
          <Info icon={Phone} label="Phone" value={user?.phone || "—"} />
          {user?.company_name && <Info icon={Building2} label="Company" value={user.company_name} />}
          <Info icon={Shield} label="Country" value={user?.country || "IN"} />
        </div>
        <button onClick={async () => { await logout(); navigate("/login"); }} className="mt-6 inline-flex items-center gap-2 rounded-xl border border-rose-200 px-4 py-2.5 text-sm font-medium text-rose-600 hover:bg-rose-50" data-testid="profile-logout">
          <LogOut className="h-4 w-4" /> Log out
        </button>
      </SectionCard>

      {user?.role === "customer" && <AddressBook />}
      {user?.role === "customer" && (
        <SectionCard title="Saved Places" className="mt-5">
          <SavedPlacesManager />
        </SectionCard>
      )}
      {user?.role === "customer" && (
        <SectionCard title="Price Drop Alerts" className="mt-5">
          <WatchedPrices />
        </SectionCard>
      )}
      {user?.role === "travel_agent" && <SellerBusinessAddress mobileReq={mobileReq} onVerified={onMobileVerified} />}

      <SectionCard title="Change Email Address" className="mt-5">
        {pendingEmail ? (
          <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4" data-testid="email-change-pending">
            <CheckCircle2 className="mt-0.5 h-5 w-5 text-emerald-600" />
            <div>
              <p className="text-sm font-medium text-emerald-800">Confirmation sent to {pendingEmail}</p>
              <p className="mt-1 text-xs text-emerald-700">Open that inbox and click the link to complete the change. The link expires in 24 hours. Your email stays the same until you confirm.</p>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500">Update the email you use to sign in and receive notifications. We'll send a confirmation link to the new address — your email only changes once you click it.</p>
            <form onSubmit={submitEmailChange} className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative flex-1">
                <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <input
                  type="email"
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="you@newdomain.com"
                  className="w-full rounded-xl border border-slate-200 py-2.5 pl-9 pr-3 text-sm text-slate-700 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                  data-testid="new-email-input"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center justify-center gap-2 rounded-xl brand-gradient-bg px-5 py-2.5 text-sm font-medium text-white disabled:opacity-60"
                data-testid="request-email-change-btn"
              >
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
                Send confirmation
              </button>
            </form>
          </>
        )}
      </SectionCard>

      <SectionCard title="Sign-in Activity & Security" className="mt-5">
        <p className="text-sm text-slate-500">Devices and locations that have signed in to your account. We alert you by email whenever a new one appears.</p>
        <div className="mt-4 space-y-2" data-testid="sessions-list">
          {sessions.length === 0 && <p className="text-sm text-slate-400">No sign-in activity recorded yet.</p>}
          {sessions.map((d) => {
            const Icon = isMobile(d.ua) ? Smartphone : Monitor;
            return (
              <div key={d.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-100 p-3" data-testid="session-row">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-500"><Icon className="h-4 w-4" /></span>
                  <div className="min-w-0">
                    {editId === d.id ? (
                      <div className="flex items-center gap-2">
                        <input value={editName} onChange={(e) => setEditName(e.target.value)} autoFocus placeholder="e.g. My work laptop" className="rounded-lg border border-slate-200 px-2 py-1 text-sm outline-none focus:border-blue-400" data-testid="device-name-input" />
                        <button onClick={() => renameDevice(d.id)} data-testid="save-device-name" className="text-xs font-medium text-blue-600">Save</button>
                        <button onClick={() => setEditId(null)} className="text-xs text-slate-400">Cancel</button>
                      </div>
                    ) : (
                      <p className="text-sm font-medium text-slate-700 truncate">
                        {d.name || deviceLabel(d.ua)}{d.country ? ` · ${d.country}` : ""}
                        {d.current && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">This device</span>}
                        <button onClick={() => { setEditId(d.id); setEditName(d.name || ""); }} data-testid={`rename-device-${d.id}`} title="Rename device" className="ml-2 align-middle text-slate-300 hover:text-blue-500"><Pencil className="inline h-3 w-3" /></button>
                      </p>
                    )}
                    <p className="text-xs text-slate-400">Last active {d.last_seen ? new Date(d.last_seen).toLocaleString("en-GB") : "—"}{d.ip ? ` · ${d.ip}` : ""}</p>
                  </div>
                </div>
                {!d.current && (
                  <button onClick={() => forgetDevice(d.id)} data-testid={`forget-device-${d.id}`} title="Remove this device" className="shrink-0 rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:border-rose-300 hover:text-rose-600">
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            );
          })}
        </div>
        <button onClick={signOutAll} data-testid="sign-out-all-btn" className="mt-4 inline-flex items-center gap-2 rounded-xl border border-rose-200 px-4 py-2.5 text-sm font-medium text-rose-600 hover:bg-rose-50">
          <ShieldCheck className="h-4 w-4" /> Sign out of all devices
        </button>
        <p className="mt-2 text-xs text-slate-400">For your security this signs you out everywhere — you'll need to log in again on each device.</p>

        <div className="mt-6 border-t border-slate-100 pt-5" data-testid="security-changes">
          <p className="text-sm font-semibold text-slate-700">Recent security changes</p>
          <p className="mt-0.5 text-xs text-slate-400">Password resets, email changes and sign-outs on your account. If you don't recognise one, reset your password right away.</p>
          <div className="mt-3 space-y-2">
            {secEvents.length === 0 && <p className="text-sm text-slate-400" data-testid="security-changes-empty">No security changes recorded yet.</p>}
            {secEvents.map((e, i) => (
              <div key={i} className="flex items-start justify-between gap-3 rounded-xl border border-slate-100 p-3" data-testid="security-change-row">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-slate-700">{SEC_LABEL[e.kind] || e.kind}</p>
                  {e.detail && <p className="text-xs text-slate-500 truncate">{e.detail}</p>}
                </div>
                <p className="shrink-0 text-xs text-slate-400">{e.at ? new Date(e.at).toLocaleString("en-GB") : ""}{e.ip ? ` · ${e.ip}` : ""}</p>
              </div>
            ))}
          </div>
        </div>
      </SectionCard>

      {company && (
        <SectionCard title="Company Information" className="mt-5">
          <div className="grid gap-3 sm:grid-cols-2 text-sm">
            <Info icon={Building2} label="Legal Name" value={company.legal_name} />
            <Info icon={MapPin} label="Address" value={company.address} />
            <Info icon={Mail} label="Support" value={company.support_email} />
            <Info icon={Phone} label="Phone" value={company.phone} />
            <Info icon={Shield} label="GSTIN" value={company.gstin} />
            <Info icon={Shield} label="PAN" value={company.pan} />
          </div>
        </SectionCard>
      )}
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 rounded-xl border border-slate-100 p-3">
      <Icon className="mt-0.5 h-4 w-4 text-blue-500" />
      <div><p className="text-xs text-slate-400">{label}</p><p className="text-sm font-medium text-slate-700">{value}</p></div>
    </div>
  );
}

function AddressBook() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null); // "new" | address_id | null
  const [form, setForm] = useState(ADDR_BLANK);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/marketplace/addresses").then((r) => setList(r.data.addresses || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const startAdd = () => { setForm(ADDR_BLANK); setEditing("new"); };
  const startEdit = (a) => { setForm({ ...ADDR_BLANK, ...a }); setEditing(a.address_id); };
  const cancel = () => setEditing(null);

  const save = async () => {
    if (!form.name || !form.line1) { toast.error("Full name and address line 1 are required"); return; }
    setSaving(true);
    try {
      if (editing === "new") await api.post("/marketplace/addresses", form);
      else await api.patch(`/marketplace/addresses/${editing}`, form);
      toast.success("Address saved"); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save address"); }
    finally { setSaving(false); }
  };
  const remove = async (id) => {
    try { await api.delete(`/marketplace/addresses/${id}`); toast.success("Address removed"); load(); }
    catch { toast.error("Could not remove address"); }
  };
  const makeDefault = async (a) => {
    try { await api.patch(`/marketplace/addresses/${a.address_id}`, { ...a, is_default: true }); toast.success("Default address updated"); load(); }
    catch { toast.error("Could not update default"); }
  };

  return (
    <SectionCard title="My Addresses" className="mt-5">
      <p className="text-sm text-slate-500">Saved delivery addresses. These are reused at checkout — set one as default for faster ordering.</p>
      <div className="mt-4 space-y-2" data-testid="address-book-list">
        {list.length === 0 && editing !== "new" && <p className="text-sm text-slate-400">No addresses saved yet.</p>}
        {list.map((a) => (
          <div key={a.address_id} className="rounded-xl border border-slate-100 p-3" data-testid={`saved-address-${a.address_id}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0 text-sm">
                <p className="font-semibold text-slate-800">
                  {a.name}{a.phone && <span className="font-normal text-slate-500"> · {a.phone}</span>}
                  {a.is_default && <span className="ml-2 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Default</span>}
                </p>
                <p className="text-slate-600">{[a.line1, a.line2, a.district, a.city, a.state, a.pincode, a.country].filter(Boolean).join(", ")}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                {!a.is_default && <button onClick={() => makeDefault(a)} title="Set as default" data-testid={`address-default-${a.address_id}`} className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:text-amber-500"><Star className="h-4 w-4" /></button>}
                <button onClick={() => startEdit(a)} title="Edit" data-testid={`address-edit-${a.address_id}`} className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:text-blue-500"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(a.address_id)} title="Delete" data-testid={`address-delete-${a.address_id}`} className="rounded-lg border border-slate-200 p-1.5 text-slate-400 hover:text-rose-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing ? (
        <div className="mt-4 rounded-xl border border-slate-200 p-3" data-testid="address-book-form">
          <AddressFields value={form} onChange={setForm} showGstin />
          <label className="mt-3 flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={!!form.is_default} onChange={(e) => setForm({ ...form, is_default: e.target.checked })} data-testid="address-set-default" /> Set as default address
          </label>
          <div className="mt-3 flex gap-2">
            <button onClick={save} disabled={saving} data-testid="address-book-save" className="inline-flex items-center gap-2 rounded-lg brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save address</button>
            <button onClick={cancel} className="rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600">Cancel</button>
          </div>
        </div>
      ) : (
        <button onClick={startAdd} data-testid="address-book-add" className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-4 py-2 text-sm font-medium text-slate-600 hover:border-blue-300"><Plus className="h-4 w-4" /> Add a new address</button>
      )}
    </SectionCard>
  );
}

function SellerBusinessAddress({ mobileReq, onVerified }) {
  const [form, setForm] = useState(null);
  const [dims, setDims] = useState({ default_weight_kg: 0.5, length_cm: 15, breadth_cm: 12, height_cm: 8 });
  const [saving, setSaving] = useState(false);
  const showVerify = mobileReq?.applies && !mobileReq?.verified;

  useEffect(() => {
    api.get("/marketplace/seller/shipping-settings").then((r) => {
      const s = r.data.settings;
      if (s) {
        setForm({ name: s.contact_name || "", phone: s.phone || "", line1: s.address || "", line2: "", city: s.city || "", district: s.district || "", state: s.state || "", pincode: s.pickup_pincode || "", country: "IN" });
        setDims({ default_weight_kg: s.default_weight_kg || 0.5, length_cm: s.length_cm || 15, breadth_cm: s.breadth_cm || 12, height_cm: s.height_cm || 8 });
      } else {
        setForm({ ...ADDR_BLANK, country: "IN" });
      }
    }).catch(() => setForm({ ...ADDR_BLANK, country: "IN" }));
  }, []);

  const save = async () => {
    if (!form.name || !form.phone || !form.line1 || !form.city || !form.state || !form.pincode) { toast.error("Fill name, phone, address, city, state and PIN"); return; }
    setSaving(true);
    try {
      const payload = {
        contact_name: form.name, phone: form.phone,
        address: [form.line1, form.line2].filter(Boolean).join(", "),
        city: form.city, state: form.state, district: form.district || null,
        pickup_pincode: form.pincode,
        default_weight_kg: Number(dims.default_weight_kg), length_cm: Number(dims.length_cm),
        breadth_cm: Number(dims.breadth_cm), height_cm: Number(dims.height_cm),
      };
      const r = await api.put("/marketplace/seller/shipping-settings", payload);
      toast.success("Business address saved");
      if (r.data?.warning) toast.message(r.data.warning);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save address"); }
    finally { setSaving(false); }
  };

  if (!form) return null;

  return (
    <SectionCard title="Business & Pickup Address" className="mt-5">
      <p className="flex items-center gap-1.5 text-sm text-slate-500"><Truck className="h-4 w-4 text-blue-500" /> This is your single pickup address — couriers collect parcels here, and it's registered automatically with the shipping partner.</p>
      {mobileReq?.verified && (
        <p data-testid="seller-mobile-verified" className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-semibold text-emerald-700"><ShieldCheck className="h-4 w-4" /> Mobile number verified</p>
      )}
      {showVerify && (
        <div className="mt-3" data-testid="seller-mobile-verify-block">
          <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-amber-700"><Smartphone className="h-4 w-4" /> Verify your mobile number{mobileReq?.strictness !== "prompt" ? " — payouts stay locked until you do" : ""}</p>
          <MobileVerifyCard onVerified={onVerified} />
        </div>
      )}
      <div className="mt-4" data-testid="seller-address-form">
        <AddressFields value={form} onChange={setForm} testidPrefix="seller-addr" />
        <div className="mt-4">
          <p className="mb-2 text-xs font-semibold text-slate-500">Default parcel (used to estimate shipping)</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div><label className="mb-1 block text-xs text-slate-500">Weight (kg)</label><Input type="number" step="0.1" value={dims.default_weight_kg} onChange={(e) => setDims({ ...dims, default_weight_kg: e.target.value })} data-testid="seller-weight" /></div>
            <div><label className="mb-1 block text-xs text-slate-500">Length (cm)</label><Input type="number" value={dims.length_cm} onChange={(e) => setDims({ ...dims, length_cm: e.target.value })} data-testid="seller-length" /></div>
            <div><label className="mb-1 block text-xs text-slate-500">Breadth (cm)</label><Input type="number" value={dims.breadth_cm} onChange={(e) => setDims({ ...dims, breadth_cm: e.target.value })} data-testid="seller-breadth" /></div>
            <div><label className="mb-1 block text-xs text-slate-500">Height (cm)</label><Input type="number" value={dims.height_cm} onChange={(e) => setDims({ ...dims, height_cm: e.target.value })} data-testid="seller-height" /></div>
          </div>
        </div>
        <button onClick={save} disabled={saving} data-testid="seller-address-save" className="mt-4 inline-flex items-center gap-2 rounded-lg brand-gradient-bg px-4 py-2 text-sm font-semibold text-white disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />} Save business address</button>
      </div>
    </SectionCard>
  );
}
